import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { ViewerToken } from '../types';

interface GeneratedLink {
  url: string;
  expiresAt: string;
}

export function ViewerLinkGenerator() {
  const [label, setLabel] = useState('Francis');
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);
  const [generated, setGenerated] = useState<GeneratedLink | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<ViewerToken[]>([]);
  const [loading, setLoading] = useState(true);

  const loadActive = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('viewer_tokens')
      .select('id, label, expires_at, created_at, revoked_at')
      .is('revoked_at', null)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });
    if (!error && data) setActive(data as ViewerToken[]);
    setLoading(false);
  };

  useEffect(() => { loadActive(); }, []);

  const generate = async () => {
    setBusy(true);
    setError(null);
    setGenerated(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');

      const res = await fetch('/api/generate-viewer-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ label, hours }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      const { url, expires_at } = await res.json();
      setGenerated({ url, expiresAt: expires_at });
      await loadActive();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    if (!confirm('Revoke this viewer link? Anyone using it will be signed out on next action.')) return;
    await supabase.from('viewer_tokens').update({ revoked_at: new Date().toISOString() }).eq('id', id);
    await loadActive();
  };

  return (
    <div className="viewer-panel">
      <h3>Viewer links</h3>
      <p className="viewer-panel-sub">
        Generate a read-only link that expires after the chosen window.
      </p>

      <div className="viewer-form">
        <label>
          Label
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Francis" />
        </label>
        <label>
          Hours until expiry
          <input
            type="number"
            min={1}
            max={168}
            value={hours}
            onChange={(e) => setHours(Math.max(1, Math.min(168, Number(e.target.value) || 24)))}
          />
        </label>
        <button type="button" onClick={generate} disabled={busy}>
          {busy ? 'Generating…' : 'Generate link'}
        </button>
      </div>

      {error && <p className="err">{error}</p>}

      {generated && (
        <div className="viewer-generated">
          <p>Share this link (expires {new Date(generated.expiresAt).toLocaleString()}):</p>
          <div className="viewer-url-row">
            <input readOnly value={generated.url} onFocus={(e) => e.currentTarget.select()} />
            <button type="button" onClick={() => navigator.clipboard.writeText(generated.url)}>
              Copy
            </button>
          </div>
        </div>
      )}

      <div className="viewer-active">
        <h4>Active links</h4>
        {loading ? (
          <p>Loading…</p>
        ) : active.length === 0 ? (
          <p className="muted">No active viewer links.</p>
        ) : (
          <ul>
            {active.map((t) => (
              <li key={t.id}>
                <span>{t.label || 'Unlabelled'}</span>
                <span className="muted">expires {new Date(t.expires_at).toLocaleString()}</span>
                <button type="button" className="danger" onClick={() => revoke(t.id)}>Revoke</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
